import { useEffect } from 'react';

import { leaveRoomPresenceRedis } from '/lib/fetchRoomPresence';

interface Options {
  /** True while this participant is in the call. */
  enabled: boolean;
  roomName?: string;
  userId?: string;
  isOwner?: boolean;
}

/**
 * Tells galaxy this participant has gone when the page goes away while they
 * are in the call: a closed tab, a reload, or a navigation.
 *
 * Leaving through the call's own Leave button already does this, on Daily's
 * `left-meeting`. A page that simply goes away sends no `left-meeting`, so
 * its presence entry stayed behind until galaxy's `end_call` or the 6-hour
 * expiry. With one entry per tab that is easy to hit: close the tab, reopen
 * the link in a new one, leave properly, and the first tab's entry still
 * lists the participant, so the next person to join is let straight in.
 *
 * `pagehide`, not `beforeunload`: it also fires on mobile Safari, and it
 * fires in this frame when the embedding page unloads. `keepalive` lets the
 * request outlive the page. A reload sends this and then registers again,
 * which is balanced.
 */
export const usePresenceLeaveOnPageClose = ({
  enabled,
  isOwner,
  roomName,
  userId,
}: Options) => {
  useEffect(() => {
    if (!enabled || !roomName) return;

    const handlePageHide = () => {
      leaveRoomPresenceRedis(undefined, roomName, userId, {
        keepalive: true,
        isOwner,
      }).catch(() => {
        // The page is going away; there is nothing left to do with a failure.
      });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, isOwner, roomName, userId]);
};
